import { forwardRef } from 'react';
import {
  Alert as MuiAlert,
  AlertTitle as MuiAlertTitle,
  Box,
  Stack as MuiStack,
  Stack,
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';

import { alertVariantToMuiSeverity } from './helpers';
import { AlertProps } from './types';

const Alert = forwardRef<HTMLDivElement, React.PropsWithChildren<AlertProps>>(
  (
    {
      actionComponent,
      children,
      footer,
      isFullWidth = false,
      onClose,
      title,
      variant = 'default',
    },
    ref,
  ) => {
    const theme = useTheme();

    const severity = alertVariantToMuiSeverity[variant];

    return (
      <Stack
        sx={[
          {
            border: '1px',
            borderColor: theme.palette.colors.grey[200],
            borderRadius: '5px',
            borderStyle: 'solid',
            width: 'fit-content',
          },
        ]}
      >
        <MuiAlert
          icon={false}
          ref={ref}
          severity={severity}
          action={actionComponent}
          sx={[
            {
              '& .MuiAlert-action': {
                alignItems: footer ? 'flex-start' : 'center',
                color: theme.palette.colors.grey[600],
                paddingTop: 0,
              },
              backgroundColor: theme.palette.colors.white,
              borderLeftColor: `${severity}.main`,
              borderLeftStyle: 'solid',
              borderLeftWidth: '10px',
              boxShadow: 3,
              color: theme.palette.colors.grey[600],
              lineHeight: '20px',
              padding: '8px 16px',
              typography: 'font12',
            },
            isFullWidth && {
              '& .MuiAlert-message': {
                width: '100%',
              },
            },
            !isFullWidth && {
              width: 'fit-content',
            },
          ]}
          onClose={onClose}
        >
          <Box
            display='flex'
            alignItems='center'
            justifyContent='space-between'
          >
            <MuiAlertTitle
              sx={{
                color: theme.palette.colors.black,
                marginBottom: footer ? 0.5 : 0,
                typography: 'font14Bold',
              }}
            >
              {title}
            </MuiAlertTitle>
          </Box>
          {(children || footer) && (
            <MuiStack>
              {children}
              {footer && <AlertFooter>{footer}</AlertFooter>}
            </MuiStack>
          )}
        </MuiAlert>
      </Stack>
    );
  },
);

const AlertFooter = styled('div')`
  display: flex;
  justify-content: end;
  padding-top: 8px;
`;

Alert.displayName = 'Alert';

export default Alert;
